<template>
	<Accordion
		variant="accordion--vertical2"
		:isExpandable="totalCompanies > 0"
		:isOpen="isOpenStatus"
		@expanded="onAccordionExpanded"
		ref="accordion"
	>
		<template #main>
			<Card class="client-card" :highlights="exactMatch" ref="card">
				<template #content>
					<div class="client-card__name-copy">
						<span
							class="client-card__name"
							v-html="displayCustomerName"
						/>
					</div>
				</template>
				<template #actions>
					<Toggle
						:value="allFeaturesActive"
						@change="activateDeactivateAll"
						:label="'Activate All'"
						:isLabelBefore="true"
						v-if="showActiveAllToogle"
					>
						<template slot="true-value">YES</template>
						<template slot="false-value">NO</template>
					</Toggle>
					<Toggle
						v-model="clientData.activeBetaFeature"
						@change="updateValue"
						variant="condensed"
						v-else
					>
						<template slot="true-value">ON</template>
						<template slot="false-value">OFF</template>
					</Toggle>
				</template>
			</Card>
		</template>
		<template #body-content>
			<ul
				v-if="totalCompanies > 0"
				class="client-card__company-card-list"
			>
				<li
					class="client-card__company-card-list-item"
					v-for="(company, index) in clientData.companies"
					:key="company.id"
				>
					<CompanyCard
						:company="company"
						:search="search"
						ref="company"
						@company-updated="handleCompanyUpdated(index, $event)"
						:isLastCompany="index === totalCompanies - 1"
					/>
				</li>
			</ul>
		</template>
	</Accordion>
</template>

<script>
import Accordion from '@/components/shells/accordion/accordion';
import Card from '@/components/shells/card/card';
import CompanyCard from './company-card';
import Toggle from '@/components/forms/selection-control/toggle/toggle';

//libraries
import _ from 'lodash';
import { createTextValueObj } from '@/utils/object';

//mixins
import AccordionStatus from '@/mixins/ccl-selector/accordions-status-mixin';

export default {
	name: 'ClientCard',
	components: {
		Accordion,
		Card,
		CompanyCard,
		Toggle
	},
	mixins: [AccordionStatus],
	props: {
		client: {
			type: Object,
			default: () => {}
		},
		search: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			clientData: JSON.parse(JSON.stringify(this.client)),
			isAllActive: true
		};
	},
	created() {
		if (!this.client.companies.length) {
			return;
		}

		if (this.search) {
			const hasCompaniesLocationsMatch = this.searchInClient();
			this.onAccordionExpanded(hasCompaniesLocationsMatch);
		} else {
			const hasAccordionStoredStatus = this.openStatusStored(
				'clientsAccordionStatuses',
				this.client.id
			);

			if (hasAccordionStoredStatus) {
				this.isOpenStatus = true;
			}
		}
	},
	computed: {
		displayCustomerName() {
			const name = `${this.clientData.customerNumber} (${this.clientData.customerName})`;
			return createTextValueObj(name, this.search);
		},
		exactMatch() {
			if (this.search && this.clientData.customerName) {
				return (
					this.search &&
					this.clientData.customerName.toLowerCase() === this.search
				);
			}
			return false;
		},
		totalCompanies() {
			return _.get(this, 'client.companies.length', 0);
		},
		showActiveAllToogle() {
			return this.totalCompanies > 0;
		},
		allFeaturesActive() {
			let allActive = true;
			this.clientData.companies.forEach(company => {
				if (!company.activeBetaFeature) {
					allActive = false;
				}
				company.locations.forEach(location => {
					if (!location.activeBetaFeature) {
						allActive = false;
					}
				});
			});

			return allActive;
		}
	},
	methods: {
		onAccordionExpanded(status) {
			this.updateAccordionStatuses({
				id: this.client.id,
				status,
				name: 'clientsAccordionStatuses'
			});

			this.isOpenStatus = status;
		},
		updateBetaFeatureValue() {
			this.clientData.activeBetaFeature = this.allFeaturesActive;
			this.$emit('client-updated', this.clientData);
		},
		updateValue() {
			this.$emit('client-updated', this.clientData);
		},
		activateDeactivateAll(value) {
			this.clientData.companies.forEach(company => {
				company.activeBetaFeature = value;
				company.locations.forEach(location => {
					location.activeBetaFeature = value;
				});
			});
			this.updateBetaFeatureValue();
		},
		handleCompanyUpdated(companyIndex) {
			if (companyIndex !== -1) {
				this.updateBetaFeatureValue();
			}
		},
		searchInClient() {
			let searchTerm = '';

			if (this.search) {
				searchTerm = this.search.toLowerCase();
			}

			const match = value =>
				value &&
				value
					.toString()
					.toLowerCase()
					.includes(searchTerm);

			if (!_.isEmpty(this.client.companies)) {
				for (const company of this.client.companies) {
					if (
						match(company.companyNumber) ||
						match(company.companyName)
					) {
						return true;
					}

					if (!_.isEmpty(company.locations)) {
						for (const location of company.locations) {
							if (
								match(location.locationNumber) ||
								match(location.locationName)
							) {
								return true;
							}
						}
					}
				}
			}

			return false;
		}
	},
	watch: {
		search: {
			handler(value) {
				if (value) {
					const hasCompaniesLocationsMatch = this.searchInClient();
					this.onAccordionExpanded(hasCompaniesLocationsMatch);
				}
			}
		}
	}
};
</script>

<style lang="scss">
@import '@/scss/5-objects/_cards.scss';
@import '@/scss/5-objects/_form.scss';

.client-card {
	display: grid;
	grid-template-columns: 1fr auto;
	align-items: center;
	@include card('ccl-card');

	&__name {
		font-size: $heading-font-size-sm;
		@include mq($until: md) {
			@include ellipsis;
			display: block;
			margin-right: $gutter-sm;

			&:focus,
			&:hover,
			&:active {
				white-space: normal;
				width: auto;
				overflow: visible;
			}
		}

		text-transform: capitalize;
	}

	&__name-copy {
		display: flex;
		align-items: center;
	}

	&__company-card-list {
		display: flex;
		flex-direction: column;
	}

	&__company-card-list-item {
		&:first-child {
			margin-top: $gutter-md;
		}
		&:last-child {
			margin-bottom: $gutter-lg;
		}
	}
}
</style>
